// CustomInspectorMUI.js
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line no-unused-vars
import * as joint from '@joint/plus';
import AddIcon from '@mui/icons-material/Add';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
// Icônes Material UI
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import './css/inspector.scss';
import * as appShapes from './shapes/app-shapes';

/**
 * Composant React (fichier .js) pour gérer un lien sortant unique depuis `cell`.
 * + Edition du label en direct (optionnel, si tu souhaites ajouter un champ label).
 * + "Mode Viseur" = clic dans le diagramme pour définir la cible.
 *
 * @param {joint.dia.Graph}    graph           - Le graph JointJS
 * @param {joint.dia.Paper|null} paper        - Le paper JointJS
 * @param {joint.dia.Cell|null}  cell         - La cellule inspectée (source)
 * @param {object|null}        inspectorService - Pour manipuler isSelectingDestination et bloquer la sélection
 * @param {function}           onInspectorChange - Callback when inspector values change
 * @param {boolean}            readOnly        - If true, all controls are disabled
 */
function CustomInspectorMUI({ graph, paper, cell, inspectorService, onInspectorChange, readOnly = false }) {
  const [elements, setElements] = useState([]); // liste de toutes les cellules
  const [targets, setTargets] = useState([]);
  const [isSelectingByClick, setIsSelectingByClick] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const isDivShape = !!cell && (cell.get('type') === 'appShapes.Div' || cell.get('name') === 'DIV');
  const isDebType = !!cell && cell.get('name') === 'DEB';
  const isEchType = !!cell && cell.get('name') === 'ECH';
  // Read any override; if it's a finite number, use it, otherwise for Divs assume infinite, else 1
  const rawMax = cell ? cell.get('maxDestinations') : undefined;
  const maxDestinations =
    typeof rawMax === 'number' && Number.isFinite(rawMax)
      ? rawMax
      : isDebType || isEchType
        ? 2
        : isDivShape
          ? Infinity
          : 1;

  // 1) Charger la liste des éléments
  useEffect(() => {
    if (!graph) return;

    function refreshElements() {
      setElements(graph.getElements());
    }
    graph.on('add remove', refreshElements);
    refreshElements(); // initial call

    return () => {
      graph.off('add remove', refreshElements);
    };
  }, [graph]);

  // 2) Trouver tous les liens sortants pour une Div, ou le lien unique pour les autres types
  const findOutgoingLinks = useCallback(() => {
    if (!graph || !cell) return [];
    return graph.getConnectedLinks(cell, { outbound: true });
  }, [graph, cell]);

  // 2.5) Helper — does a given (cellId, portId) already have any connection?
  const hasAnyConnectionOnPort = useCallback(
    (cellId, portId) => {
      if (!graph || !cellId || !portId) return false;
      return graph.getLinks().some((l) => {
        const s = l.get('source') || {};
        const t = l.get('target') || {};
        return (s.id === cellId && s.port === portId) || (t.id === cellId && t.port === portId);
      });
    },
    [graph]
  );

  // 3) Charger les cibles actuelles
  useEffect(() => {
    if (!cell || !graph) {
      setTargets([]);
      return;
    }

    const links = findOutgoingLinks();
    const newTargets = links.map((link) => ({
      id: link.get('target')?.id,
      linkId: link.id,
    }));

    setTargets(newTargets);
  }, [cell, graph, findOutgoingLinks]);

  // Short-circuit all mutating operations in readOnly
  const safeCreateLink = (...args) => {
    if (readOnly) return;
    return createLink(...args);
  };
  const safeRemoveLink = (...args) => {
    if (readOnly) return;
    return removeLink(...args);
  };
  const safeActivateViseurMode = (...args) => {
    if (readOnly) return;
    return activateViseurMode(...args);
  };

  // 4) Créer un nouveau lien
  function createLink(newTargetId, targetPort = null) {
    if (!cell || !graph || !newTargetId) return;

    const targetCell = graph.getCell(newTargetId);
    if (!targetCell) {
      console.warn('[CustomInspectorMUI] Cible introuvable => skip');
      return;
    }

    // ⛔ Guard: if we are explicitly targeting a Tri port that already has a link, abort
    if (targetPort && targetCell.get('type') === 'appShapes.Tri') {
      if (hasAnyConnectionOnPort(targetCell.id, targetPort)) {
        console.error(
          `[CustomInspectorMUI] Port "${targetPort}" on Tri(${targetCell.id}) already has a connection. Aborting.`
        );
        return; // do NOT create the link
      }
    }

    // Recalculer la liste filtrée et déterminer s'il n'y a qu'une seule option
    const localFilteredEls = elements.filter((el) => cell && el.id !== cell.id);
    const localOnlyOneOption = localFilteredEls.length === 1;

    // Si le nombre maximum de destinations est 1 ou s'il n'existe qu'une seule option de destination,
    // on supprime les anciens liens pour ne conserver qu'un unique lien.
    if (maxDestinations === 1 || localOnlyOneOption) {
      const oldLinks = findOutgoingLinks();
      oldLinks.forEach((link) => link.remove());
    }

    const link = new appShapes.app.Link();
    link.source(cell);

    if (targetPort) {
      link.target({ id: targetCell.id, port: targetPort });
      try {
        if (targetCell.get('type') === 'appShapes.Tri') {
          targetCell.portProp(targetPort, 'source', cell.id);
          targetCell.trigger('change:ports', targetCell, targetCell.get('ports'));
        }
      } catch (e) {
        console.warn('[CustomInspectorMUI] Failed to set source on Tri port', e);
      }
    } else {
      link.target(targetCell);
    }

    link.router('normal');

    link.attr({
      'line/stroke': '#000000',
      'line/strokeWidth': 2,
      'line/targetMarker': {
        type: 'path',
        d: 'M 0 -5 10 0 0 5 z',
        fill: '#000000',
        stroke: 'none',
      },
    });

    link.addTo(graph);

    if (maxDestinations === 1 || localOnlyOneOption) {
      // Si une seule destination est possible ou autorisée, on remplace les cibles existantes
      setTargets([{ id: newTargetId, linkId: link.id }]);
    } else {
      setTargets((prev) => [...prev, { id: newTargetId, linkId: link.id }]);
    }
  }

  function removeLink(linkId) {
    const link = graph.getCell(linkId);
    if (link) link.remove();
    setTargets((prev) => prev.filter((t) => t.linkId !== linkId));
  }

  function activateViseurMode(existingLinkId = null) {
    if (!paper || !inspectorService) return;
    setIsSelectingByClick(true);
    paper.el.style.cursor = 'crosshair';
    inspectorService.isSelectingDestination = true;

    const handleClick = (cellView, evt, x, y) => {
      const targetCell = cellView.model;
      if (targetCell.id === cell?.id) {
        console.warn('[CustomInspectorMUI] source=target => skip');
        deactivateViseurMode();
        return;
      }

      // Vérifier si la cible est un anneau de tri
      if (targetCell.get('type') === 'appShapes.Tri') {
        // Click must be on a port circle (has a "port" attribute)
        const clickedPort = evt.target.getAttribute('port');
        if (!clickedPort) {
          console.warn('[CustomInspectorMUI] Tri element clicked but not on a port => skip');
          return;
        }

        if (hasAnyConnectionOnPort(targetCell.id, clickedPort)) {
          console.error(
            `[CustomInspectorMUI] Port "${clickedPort}" on Tri(${targetCell.id}) already has a connection. Aborting.`
          );
          deactivateViseurMode();
          return;
        }
      }

      if (existingLinkId) {
        // Mettre à jour le lien existant
        const link = graph.getCell(existingLinkId);
        if (link) {
          // 1) If the old target was a Tri port, clear its `source` if it points to this cell
          try {
            const oldTarget = link.get('target');
            if (oldTarget?.id && oldTarget?.port) {
              const oldTargetCell = graph.getCell(oldTarget.id);
              if (oldTargetCell?.get('type') === 'appShapes.Tri') {
                const oldPortId = oldTarget.port;
                const currentSource = (oldTargetCell.getPort(oldPortId) || {}).source;
                if (currentSource === cell.id) {
                  oldTargetCell.portProp(oldPortId, 'source', null);
                  oldTargetCell.trigger('change:ports', oldTargetCell, oldTargetCell.get('ports'));
                }
              }
            }
          } catch (e) {
            console.warn('[CustomInspectorMUI] Failed to clear source on previous Tri port', e);
          }

          if (targetCell.get('type') === 'appShapes.Tri') {
            const newPortId = evt.target.getAttribute('port');
            // Guard already checked above, safe to retarget
            link.target({ id: targetCell.id, port: newPortId });
            try {
              targetCell.portProp(newPortId, 'source', cell.id);
              targetCell.trigger('change:ports', targetCell, targetCell.get('ports'));
            } catch (e) {
              console.warn('[CustomInspectorMUI] Failed to set source on new Tri port', e);
            }
          } else {
            link.target(targetCell);
          }

          setTargets((prev) => prev.map((t) => (t.linkId === existingLinkId ? { ...t, id: targetCell.id } : t)));
        }
      } else {
        // Créer un nouveau lien
        if (targetCell.get('type') === 'appShapes.Tri') {
          const newPortId = evt.target.getAttribute('port');
          // Guard already checked above
          safeCreateLink(targetCell.id, newPortId);
        } else {
          safeCreateLink(targetCell.id);
        }
      }
      deactivateViseurMode();
    };

    // Écouter à la fois les clics sur les cellules et sur les ports
    paper.on('element:pointerclick', handleClick);
    paper.on('element:magnet:pointerclick', handleClick);

    // Stocker les fonctions de callback pour le nettoyage
    paper._viseurCallbacks = {
      element: handleClick,
      magnet: handleClick,
    };
  }

  function deactivateViseurMode() {
    if (!paper || !inspectorService) return;
    setIsSelectingByClick(false);
    paper.el.style.cursor = 'default';
    inspectorService.isSelectingDestination = false;

    // Nettoyer tous les écouteurs d'événements
    if (paper._viseurCallbacks) {
      paper.off('element:pointerclick', paper._viseurCallbacks.element);
      paper.off('element:magnet:pointerclick', paper._viseurCallbacks.magnet);
      paper._viseurCallbacks = null;
    }
  }

  // Add a new function to handle any inspector value changes
  const handleInspectorChange = useCallback(() => {
    if (onInspectorChange) {
      onInspectorChange();
    }
  }, [onInspectorChange]);

  // Add event listeners for cell attribute changes
  useEffect(() => {
    if (!cell) return;

    const onChange = () => {
      handleInspectorChange();
    };

    cell.on('change:attrs change:customAttributes', onChange);

    return () => {
      cell.off('change:attrs change:customAttributes', onChange);
    };
  }, [cell, handleInspectorChange]);

  // Si aucune cellule sélectionnée => on n'affiche rien d'autre
  if (!cell) {
    return null;
  }

  // Ne pas afficher le CustomInspector pour les anneaux de tri ou les liens
  const supportedTypes = ['appShapes.Div', 'appShapes.Stock', 'standard.Rectangle'];
  if (!supportedTypes.includes(cell.get('type')) || cell.isLink()) return null;

  // Filtrer la source => pas de lien vers soi-même
  const sourceLabel = cell.attr('label/text') || `ID: ${cell.id}`;

  return (
    <Paper className="custom-inspector" elevation={3}>
      <Box data-cy="inspector-content">
        <Typography variant="body2">
          Source: <strong>{sourceLabel}</strong>
        </Typography>

        {/* Liste des liens existants */}
        {targets.map((target) => (
          <Box key={target.linkId} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              variant="body2"
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mr: 0.5,
              }}
            >
              {(() => {
                const targetCell = graph.getCell(target.id);
                const cellLabel = targetCell?.attr('label/text') || `ID: ${target.id}` || 'None';
                const cellName = targetCell?.get('cellName');
                return cellName ? `${cellName} (${cellLabel})` : cellLabel;
              })()}
            </Typography>
            <IconButton
              color="primary"
              onClick={readOnly ? undefined : () => safeActivateViseurMode(target.linkId)}
              disabled={readOnly || isSelectingByClick}
              size="small"
              sx={{ mr: 0.5 }}
            >
              <GpsFixedIcon />
            </IconButton>
            <IconButton
              onClick={readOnly ? undefined : () => safeRemoveLink(target.linkId)}
              color="error"
              size="small"
              disabled={readOnly}
            >
              <HighlightOffIcon />
            </IconButton>
          </Box>
        ))}

        {/* Interface d'ajout de lien */}
        {targets.length < maxDestinations && (
          <>
            {isAddingNew ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 0.5 }}
                >
                  Cliquez sur l’icône pour choisir une destination
                </Typography>
                <IconButton
                  color="primary"
                  onClick={
                    readOnly
                      ? undefined
                      : () => {
                          safeActivateViseurMode();
                          setIsAddingNew(false);
                        }
                  }
                  disabled={readOnly || isSelectingByClick}
                  size="small"
                  sx={{ mr: 0.5 }}
                >
                  <GpsFixedIcon />
                </IconButton>
                <IconButton
                  onClick={readOnly ? undefined : () => setIsAddingNew(false)}
                  color="error"
                  size="small"
                  disabled={readOnly}
                >
                  <HighlightOffIcon />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <IconButton
                  color="primary"
                  onClick={readOnly ? undefined : () => setIsAddingNew(true)}
                  disabled={readOnly}
                  size="small"
                  sx={{
                    display: 'flex',
                    gap: 1,
                    border: '1px dashed',
                    borderRadius: 1,
                    width: '100%',
                    justifyContent: 'center',
                    py: 0.5,
                  }}
                >
                  <AddIcon fontSize="small" />
                  <Typography variant="caption">Ajouter une destination</Typography>
                </IconButton>
              </Box>
            )}
          </>
        )}
        {isSelectingByClick && !readOnly && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <IconButton color="error" onClick={deactivateViseurMode} size="small" sx={{ mr: 1 }}>
              <HighlightOffIcon />
            </IconButton>
            <Typography variant="caption">Cliquez sur un élément pour créer le lien</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

CustomInspectorMUI.propTypes = {
  graph: PropTypes.object,
  paper: PropTypes.object,
  cell: PropTypes.object,
  inspectorService: PropTypes.shape({
    isSelectingDestination: PropTypes.bool,
  }),
  onInspectorChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

CustomInspectorMUI.defaultProps = {
  graph: null,
  paper: null,
  cell: null,
  inspectorService: null,
  onInspectorChange: null,
  readOnly: false,
};

export default CustomInspectorMUI;
